"use client";

import React, { createContext, useContext, useCallback, ReactNode } from "react";
import { BrowserHooks } from "@/src/hook";

interface ActionBarDrawerContextType {
  getDrawerData: (drawerId: string) => any;
  setDrawerData: (drawerId: string, data: any) => void;
  clearDrawerData: (drawerId: string) => void;
  updateDrawerData: (drawerId: string, updates: any) => void;
}

const ActionBarDrawerContext = createContext<ActionBarDrawerContextType | null>(null);

interface ActionBarDrawerProviderProps {
  children: ReactNode;
}

export function ActionBarDrawerProvider({ children }: ActionBarDrawerProviderProps) {
  const [drawerStorage, setDrawerStorage] = BrowserHooks.useLocalStorage("action-bar-drawer-storage", {});

  const getDrawerData = useCallback(
    (drawerId: string) => {
      return drawerStorage[drawerId] || {};
    },
    [drawerStorage]
  );

  const setDrawerData = useCallback(
    (drawerId: string, data: any) => {
      setDrawerStorage((prev: any) => ({
        ...prev,
        [drawerId]: data,
      }));
    },
    [setDrawerStorage]
  );

  const clearDrawerData = useCallback(
    (drawerId: string) => {
      setDrawerStorage((prev: any) => {
        const newStorage = { ...prev };
        delete newStorage[drawerId];
        return newStorage;
      });
    },
    [setDrawerStorage]
  );

  const updateDrawerData = useCallback(
    (drawerId: string, updates: any) => {
      setDrawerStorage((prev: any) => ({
        ...prev,
        [drawerId]: {
          ...prev[drawerId],
          ...updates,
        },
      }));
    },
    [setDrawerStorage]
  );

  const value = {
    getDrawerData,
    setDrawerData,
    clearDrawerData,
    updateDrawerData,
  };

  return <ActionBarDrawerContext.Provider value={value}>{children}</ActionBarDrawerContext.Provider>;
}

export function useActionBarDrawer() {
  const context = useContext(ActionBarDrawerContext);
  if (!context) {
    throw new Error("useActionBarDrawer must be used within an ActionBarDrawerProvider");
  }
  return context;
}

export default ActionBarDrawerContext;
